import React, { useState, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import "./CSS/Shopcategory.css";
import { useProductsByCategory } from '../hooks/useProducts';
import { useCategory } from '../hooks/useCategories';
import Item from '../componnents/Item/item';
import { ProductSkeleton, EmptyState } from '../componnents/UI';
import {
  getProductImageUrl,
  getProductId,
  getProductName,
  getProductPrices,
  getCategoryImageUrl,
  isProductInStock,
} from '../utils/helpers';
import { 
  Filter, X, ChevronDown, ChevronUp, SlidersHorizontal, 
  Grid3X3, List, Star, Loader2
} from 'lucide-react';
import { apiService } from '../services/api';
import { translateProducts } from '../utils/translateData';

const CategoryPage = () => {
  const { t } = useTranslation(['search', 'common']);
  const { categoryId } = useParams();
  const { data: category } = useCategory(categoryId);
  const { data, isLoading, error } = useProductsByCategory(categoryId, { limit: 20, page: 1 });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [allProducts, setAllProducts] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  
  // Filter & View state
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [minRating, setMinRating] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [expandedSections, setExpandedSections] = useState({
    price: true,
    rating: false,
  });
  
  // Handle various API response formats
  const getProductsArray = (responseData) => {
    if (!responseData) return [];
    if (Array.isArray(responseData.products)) return responseData.products;
    if (Array.isArray(responseData)) return responseData;
    if (Array.isArray(responseData.data)) return responseData.data;
    if (Array.isArray(responseData.products?.data)) return responseData.products.data;
    return [];
  };
  
  // Update products when data changes
  useEffect(() => {
    if (data) {
      const newProducts = getProductsArray(data);
      if (currentPage === 1) {
        setAllProducts(newProducts);
      }
      
      // Check if there are more products
      const pagination = data.pagination;
      const total = data.total || 0;
      const currentTotal = currentPage === 1 ? newProducts.length : allProducts.length + newProducts.length;
      setHasMore(pagination?.hasMore !== false && (pagination?.hasMore || currentTotal < total || newProducts.length >= 20));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Apply filters and sorting
  const filteredProducts = useMemo(() => {
    let result = [...allProducts];

    // Filter by price
    if (minPrice) {
      result = result.filter(item => {
        const { price } = getProductPrices(item);
        return price >= Number(minPrice);
      });
    }
    if (maxPrice) {
      result = result.filter(item => {
        const { price } = getProductPrices(item);
        return price <= Number(maxPrice);
      });
    }

    // Filter by rating
    if (minRating) {
      result = result.filter(item => {
        const rating = item.averageRating || item.rating || 0;
        return rating >= Number(minRating);
      });
    }

    // Sort
    switch (sortBy) {
      case 'price_asc':
        result.sort((a, b) => {
          const priceA = getProductPrices(a).price;
          const priceB = getProductPrices(b).price;
          return priceA - priceB;
        });
        break;
      case 'price_desc':
        result.sort((a, b) => {
          const priceA = getProductPrices(a).price;
          const priceB = getProductPrices(b).price;
          return priceB - priceA;
        });
        break;
      case 'rating':
        result.sort((a, b) => {
          const ratingA = a.averageRating || a.rating || 0;
          const ratingB = b.averageRating || b.rating || 0;
          return ratingB - ratingA;
        });
        break;
      case 'popular':
        result.sort((a, b) => {
          const soldA = a.sold || a.salesCount || 0;
          const soldB = b.sold || b.salesCount || 0;
          return soldB - soldA;
        });
        break;
      case 'newest':
      default:
        // Keep original order (assumed newest first from API)
        break;
    }

    return result;
  }, [allProducts, minPrice, maxPrice, minRating, sortBy]);

  const bannerUrl = getCategoryImageUrl(category, 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1200&h=400&fit=crop');

  const clearFilters = () => {
    setMinPrice('');
    setMaxPrice('');
    setMinRating('');
    setSortBy('newest');
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const hasActiveFilters = minPrice || maxPrice || minRating || sortBy !== 'newest';

  // Price range presets
  const pricePresets = [
    { label: t('pricePresets.under25', { ns: 'search' }), min: '', max: '25' },
    { label: t('pricePresets.25to50', { ns: 'search' }), min: '25', max: '50' },
    { label: t('pricePresets.50to100', { ns: 'search' }), min: '50', max: '100' },
    { label: t('pricePresets.100to200', { ns: 'search' }), min: '100', max: '200' },
    { label: t('pricePresets.over200', { ns: 'search' }), min: '200', max: '' },
  ];

  const ratingOptions = [
    { value: '4', label: t('ratingOptions.4up', { ns: 'search' }) },
    { value: '3', label: t('ratingOptions.3up', { ns: 'search' }) },
    { value: '2', label: t('ratingOptions.2up', { ns: 'search' }) },
  ];

  const sortOptions = [
    { value: 'newest', label: t('sortOptions.newest', { ns: 'search' }) },
    { value: 'popular', label: t('sortOptions.popular', { ns: 'search' }) },
    { value: 'price_asc', label: t('sortOptions.price_asc', { ns: 'search' }) },
    { value: 'price_desc', label: t('sortOptions.price_desc', { ns: 'search' }) },
    { value: 'rating', label: t('sortOptions.rating', { ns: 'search' }) },
  ];

  return (
    <div className='shop-category'>
      {/* Category Banner */}
      <div className="category-banner-wrapper">
        <img src={bannerUrl} alt={category?.name || t('category', { defaultValue: 'Category', ns: 'search' })} className="Category-Banner" />
        <div className="category-banner-overlay">
          <h1>{category?.name || t('category', { defaultValue: 'Category', ns: 'search' })}</h1>
          {category?.description && <p>{category.description}</p>}
        </div>
      </div>

      {/* Main Content with Filters */}
      <div className="category-content">
        {/* Filters Sidebar */}
        <aside className={`category-filters ${showFilters ? 'open' : ''}`}>
          <div className="filters-header">
            <h3>
              <Filter size={18} />
              {t('filters', { ns: 'search' })}
            </h3>
            {hasActiveFilters && (
              <button className="clear-all-btn" onClick={clearFilters}>
                {t('clearAll', { ns: 'search' })}
              </button>
            )}
            <button className="close-filters" onClick={() => setShowFilters(false)}>
              <X size={24} />
            </button>
          </div>

          {/* Price Range Filter */}
          <div className="filter-section">
            <button className="filter-section-header" onClick={() => toggleSection('price')}>
              <span>{t('priceRange', { ns: 'search' })}</span>
              {expandedSections.price ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
            {expandedSections.price && (
              <div className="filter-section-content">
                <div className="price-inputs">
                  <div className="price-input-group">
                    <label>{t('min', { ns: 'search' })}</label>
                    <div className="price-input-wrapper">
                      <span className="currency">$</span>
                      <input
                        type="number"
                        value={minPrice}
                        onChange={(e) => setMinPrice(e.target.value)}
                        placeholder="0"
                        min="0"
                      />
                    </div>
                  </div>
                  <span className="price-separator">–</span>
                  <div className="price-input-group">
                    <label>{t('max', { ns: 'search' })}</label>
                    <div className="price-input-wrapper">
                      <span className="currency">$</span>
                      <input
                        type="number"
                        value={maxPrice}
                        onChange={(e) => setMaxPrice(e.target.value)}
                        placeholder={t('any', { ns: 'search' })}
                        min="0"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="price-presets">
                  {pricePresets.map((preset, idx) => (
                    <button
                      key={idx}
                      className={`price-preset ${minPrice === preset.min && maxPrice === preset.max ? 'active' : ''}`}
                      onClick={() => {
                        setMinPrice(preset.min);
                        setMaxPrice(preset.max);
                      }}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Rating Filter */}
          <div className="filter-section">
            <button className="filter-section-header" onClick={() => toggleSection('rating')}>
              <span>{t('customerRating', { ns: 'search' })}</span>
              {expandedSections.rating ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
            {expandedSections.rating && (
              <div className="filter-section-content">
                {ratingOptions.map((option) => (
                  <label 
                    key={option.value} 
                    className={`filter-option rating-option ${minRating === option.value ? 'active' : ''}`}
                  >
                    <input
                      type="radio"
                      name="rating"
                      checked={minRating === option.value}
                      onChange={() => setMinRating(minRating === option.value ? '' : option.value)}
                    />
                    <div className="rating-stars">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          size={14}
                          fill={i < Number(option.value) ? '#fbbf24' : 'none'}
                          stroke={i < Number(option.value) ? '#fbbf24' : '#d1d5db'}
                        />
                      ))}
                    </div>
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Apply Filters Button (Mobile) */}
          <button className="apply-filters-btn" onClick={() => setShowFilters(false)}>
            {t('showResults', { count: filteredProducts.length, ns: 'search' })}
          </button>
        </aside>

        {/* Products Section */}
        <main className="category-products-main">
          {/* Results Header */}
          <div className="category-results-header">
            
            <div className="results-controls">
              {/* Mobile Filter Toggle */}
              <button 
                className={`filter-toggle ${showFilters ? 'active' : ''}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <SlidersHorizontal size={18} />
                <span>{t('filters', { ns: 'search' })}</span>
                {hasActiveFilters && <span className="filter-badge"></span>}
              </button>

              {/* View Toggle */}
              <div className="view-toggle">
                <button 
                  className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                  onClick={() => setViewMode('grid')}
                  title={t('gridView', { ns: 'search' })}
                >
                  <Grid3X3 size={18} />
                </button>
                <button 
                  className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                  onClick={() => setViewMode('list')}
                  title={t('listView', { ns: 'search' })}
                >
                  <List size={18} />
                </button>
              </div>

              {/* Sort Dropdown */}
              <div className="sort-wrapper">
                <label>{t('sort', { ns: 'search' })}:</label>
                <select 
                  value={sortBy} 
                  onChange={(e) => setSortBy(e.target.value)}
                  className="sort-select"
                >
                  {sortOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Active Filters Tags */}
          {hasActiveFilters && (
            <div className="active-filters">
              {(minPrice || maxPrice) && (
                <span className="filter-tag">
                  <span className="tag-label">{t('price', { ns: 'search' })}:</span>
                  ${minPrice || '0'} – ${maxPrice || t('any', { ns: 'search' })}
                  <button onClick={() => { setMinPrice(''); setMaxPrice(''); }}><X size={14} /></button>
                </span>
              )}
              {minRating && (
                <span className="filter-tag">
                  <span className="tag-label">{t('rating', { ns: 'search' })}:</span>
                  {minRating}★ & Up
                  <button onClick={() => setMinRating('')}><X size={14} /></button>
                </span>
              )}
            </div>
          )}

          {/* Products Grid */}
          {isLoading ? (
            <div className="products-grid">
              <ProductSkeleton count={8} />
            </div>
          ) : error ? (
            <div className="category-error">
              <p>{t('failedToLoad', { ns: 'search' })}</p>
            </div>
          ) : filteredProducts.length === 0 ? (
            <EmptyState
              icon="package"
              title={t('noResults', { ns: 'search' })}
              description={hasActiveFilters 
                ? t('noResultsDescription', { ns: 'search' })
                : t('noProductsInCategory', { defaultValue: 'No products available in this category yet', ns: 'search' })
              }
              actionText={hasActiveFilters ? t('clearFilters', { ns: 'search' }) : undefined}
              onAction={hasActiveFilters ? clearFilters : undefined}
            />
          ) : (
            <>
              <div className={`products-${viewMode}`}>
                {filteredProducts.map((item, i) => {
                  const { price, comparePrice, fromPrice } = getProductPrices(item);
                  return (
                    <Item
                      key={getProductId(item) || i}
                      id={getProductId(item)}
                      image={getProductImageUrl(item)}
                      name={getProductName(item)}
                      newPrice={price}
                      oldPrice={comparePrice}
                      fromPrice={fromPrice}
                      sold={item.sold || item.salesCount || 0}
                      rating={item.averageRating || item.rating || 4.5}
                      shipping={item.freeShipping ? t('freeShipping') : t('standardShipping')}
                      inStock={isProductInStock(item)}
                    />
                  );
                })}
              </div>
              
              {/* Load More Button */}
              {hasMore && filteredProducts.length > 0 && (
                <div className="load-more-container" style={{ textAlign: 'center', marginTop: '2rem', padding: '1rem' }}>
                  <button
                    className="load-more-btn"
                    onClick={async () => {
                      setIsLoadingMore(true);
                      try {
                        const nextPage = currentPage + 1;
                        const response = await apiService.getProductsByCategory(categoryId, { limit: 20, page: nextPage });
                        const newProducts = translateProducts(getProductsArray(response));
                        setAllProducts(prev => [...prev, ...newProducts]);
                        setCurrentPage(nextPage);
                        
                        // Check if there are more products
                        const pagination = response.pagination;
                        const total = response.total || 0;
                        const currentTotal = allProducts.length + newProducts.length;
                        setHasMore(pagination?.hasMore !== false && (pagination?.hasMore || currentTotal < total || newProducts.length >= 20));
                      } catch (err) {
                        console.error('Failed to load more products:', err);
                        setHasMore(false);
                      } finally {
                        setIsLoadingMore(false);
                      }
                    }}
                    disabled={isLoadingMore}
                    style={{
                      padding: '0.75rem 2rem',
                      fontSize: '1rem',
                      fontWeight: 500,
                      color: '#fff',
                      backgroundColor: '#2563eb',
                      border: 'none',
                      borderRadius: '0.5rem',
                      cursor: isLoadingMore ? 'not-allowed' : 'pointer',
                      opacity: isLoadingMore ? 0.7 : 1,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    {isLoadingMore ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        {t('loadingMore', { ns: 'common' })}
                      </>
                    ) : (
                      t('loadMore', { ns: 'common' })
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default CategoryPage;
